import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import advancedFormat from "dayjs/plugin/advancedFormat";

import {
  sumTonsCo2e,
  splitAggregatedTransactionsBreakdownSubcategories
} from "@aclymatepackages/other-helpers";
import {
  buildScopesRealDataObj,
  buildEmissionGroupData
} from "@aclymatepackages/chart-helpers";

import {
  subcategories,
  otherSubcategoryObj
} from "@aclymatepackages/subcategories";

import { useAccountData, useCollectionDataListener } from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";
import useDisplayMeasurementSystem from "../hooks/displayMeasurementSystem";

import usePlatformLayoutContext from "../contexts/platformLayout";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(quarterOfYear);
dayjs.extend(advancedFormat);

const sumEmissionsTonsBySubcategory = (emissions, subcategory) => {
  const subcategoryEmissions = emissions.filter(
    (emission) => emission.subcategory === subcategory
  );
  return Math.abs(sumTonsCo2e(subcategoryEmissions));
};

const buildSubcategoriesDataObj = (emissionsArray) => {
  return Object.fromEntries(
    [...subcategories, otherSubcategoryObj].map(({ subcategory }) => {
      const emissionsSum = sumEmissionsTonsBySubcategory(
        emissionsArray,
        subcategory
      );
      return [subcategory, emissionsSum];
    })
  );
};

export const useTransactionsMonthsData = () => {
  const [{ startDate }] = useAccountData();

  const [transactionsMonths = [], transactionsMonthsLoading] =
    useCollectionDataListener("all-aggregated-transactions", [
      ["date", ">=", startDate]
    ]);

  return [
    splitAggregatedTransactionsBreakdownSubcategories(transactionsMonths),
    transactionsMonthsLoading
  ];
};

export const useUpdateTransactions = () => {
  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();

  const updateTransactions = async (transactionsToUpdate) => {
    const companyId = getAccountId();

    return await fetchOurApi({
      path: `/transactions/update-transactions`,
      method: "POST",
      data: {
        companyId,
        transactions: transactionsToUpdate
      },
      callback: () => {
        triggerTransactionUpdateCountdown();
      }
    });
  };

  return updateTransactions;
};

const findObjectValuesSum = (object) =>
  Object.values(object).reduce((sum, value) => sum + value, 0);

const buildGroupedChartData = ({ groupedEmissions, viewMode }) => {
  const buildRealDataObj =
    viewMode === "subcategories"
      ? buildSubcategoriesDataObj
      : buildScopesRealDataObj;

  return groupedEmissions.map((emissionsArray, idx) => {
    const subcategoriesObj = buildRealDataObj(emissionsArray, [
      ...subcategories,
      otherSubcategoryObj
    ]);

    const buildWarningObj = () => {
      const singleEmissionWarning = emissionsArray.find(
        ({ warning }) => !!warning
      );

      if (!singleEmissionWarning) {
        return null;
      }

      const { warning } = singleEmissionWarning;
      return warning;
    };

    return {
      ...subcategoriesObj,
      warning: buildWarningObj(),
      id: `emissions-chart-group-${idx}`,
      emissionsSumTons: sumTonsCo2e(emissionsArray),
      totalEmissionsSumTons: findObjectValuesSum(subcategoriesObj),
      viewMode
    };
  });
};

export const useEmissionsChartData = ({
  emissions,
  viewMode = "subcategories",
  graphPeriod,
  branding,
  isPercentageChart,
  startDate: startDateOverride
}) => {
  const { convertCarbonUnits } = useDisplayMeasurementSystem();
  const [{ startDate: accountStartDate }] = useAccountData();
  const startDate = startDateOverride ?? accountStartDate;

  const {
    chartLabelsArray,
    scopesArray,
    subcategoriesArray,
    groupedEmissions
  } = buildEmissionGroupData(emissions, graphPeriod, startDate, branding);

  const buildChartData = () => {
    const preliminaryChartData = buildGroupedChartData({
      groupedEmissions,
      viewMode
    });

    const convertChartDataObject = (chartDataObj, converter) =>
      Object.fromEntries(
        Object.entries(chartDataObj).map(([key, value]) => {
          if (typeof value !== "number") {
            return [key, value];
          }
          return [key, converter(value)];
        })
      );

    const labelChartData = (chartData) =>
      chartData.map((data, idx) => ({
        ...data,
        label: chartLabelsArray[idx]
      }));

    if (isPercentageChart) {
      const percentageConvertedChartData = preliminaryChartData.map(
        (chartDataObj) => {
          const objectSubcategoryProperties = Object.keys(chartDataObj).filter(
            (key) =>
              subcategories.find(({ subcategory }) => subcategory === key)
          );
          const objectSubcategoryValues = objectSubcategoryProperties.map(
            (subcategory) => ({
              key: subcategory,
              value: chartDataObj[subcategory]
            })
          );

          const periodEmissionsSum = objectSubcategoryValues.reduce(
            (sum, { value }) => value + sum,
            0
          );

          const percentageConverter = (value) =>
            (value / periodEmissionsSum) * 100;

          const newObject = Object.fromEntries(
            objectSubcategoryValues.map(({ key, value }) => [key, value])
          );

          return convertChartDataObject(newObject, percentageConverter);
        }
      );
      return labelChartData(percentageConvertedChartData);
    }

    const unitConvertedChartData = preliminaryChartData.map((chartDataObj) =>
      convertChartDataObject(chartDataObj, convertCarbonUnits)
    );

    return labelChartData(unitConvertedChartData);
  };

  const chartData = buildChartData();

  const chartArray = viewMode === "scopes" ? scopesArray : subcategoriesArray;

  return { chartData, chartArray };
};

const getMonthKey = (transaction) => {
  const date = new Date(transaction.date);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const buildMonthDate = (monthId) => {
  const [year, month] = monthId.split("-");
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return new Date(Date.UTC(year, month - 1, lastDay)).toISOString();
};

export const aggregateTransactionsByMonth = (transactions) => {
  const groups = transactions.reduce((acc, transaction) => {
    const groupId = getMonthKey(transaction);
    const groupData = acc[groupId] || {};
    const subcategory = transaction.subcategory;

    const subcategoryGroup = groupData[subcategory] || {
      subcategory,
      transactionsCount: 0,
      tonsCo2e: 0
    };

    return {
      ...acc,
      [groupId]: {
        ...groupData,
        [subcategory]: {
          ...subcategoryGroup,
          transactionsCount: subcategoryGroup.transactionsCount + 1,
          tonsCo2e: subcategoryGroup.tonsCo2e + transaction.tonsCo2e
        }
      }
    };
  }, {});

  return Object.entries(groups).map(([monthId, subcategoryGroups]) => ({
    id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    date: buildMonthDate(monthId),
    subcategoriesBreakdown: Object.values(subcategoryGroups)
  }));
};

export const aggregateOfficeTransactions = (transactions) => {
  const getCollectionId = (tx) => tx?.office?.id || "unassigned";
  const collectionGroups = transactions.reduce((acc, transaction) => {
    const collectionId = getCollectionId(transaction);
    const existingTransactions = acc[collectionId] || [];

    return {
      ...acc,
      [collectionId]: [...existingTransactions, transaction]
    };
  }, {});

  return Object.fromEntries(
    Object.entries(collectionGroups).map(([collectionId, txs]) => [
      collectionId,
      aggregateTransactionsByMonth(txs)
    ])
  );
};

export const aggregateEmployeeTransactions = (transactions) => {
  const getEmployeeIds = (tx) => tx?.taggedEmployeesIds || [];
  const employeeGroups = transactions.reduce((acc, transaction) => {
    const employeeIds = getEmployeeIds(transaction);

    return employeeIds.reduce((innerAcc, employeeId) => {
      const existingTransactions = innerAcc[employeeId] || [];

      return {
        ...innerAcc,
        [employeeId]: [...existingTransactions, transaction]
      };
    }, acc);
  }, {});

  return Object.entries(employeeGroups).map(([employeeId, txs]) => ({
    id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    individualId: employeeId,
    aggregatedTransactions: aggregateTransactionsByMonth(txs)
  }));
};

export const aggregateVendorTransactions = (transactions, vendors) => {
  const getCollectionId = (tx) => tx?.vendorId || "unassigned";
  const collectionGroups = transactions.reduce((acc, transaction) => {
    const collectionId = getCollectionId(transaction);
    const existingTransactions = acc[collectionId] || [];

    return {
      ...acc,
      [collectionId]: [...existingTransactions, transaction]
    };
  }, {});

  const monthlyAggregatedVendors = Object.fromEntries(
    Object.entries(collectionGroups).map(([collectionId, txs]) => [
      collectionId,
      aggregateTransactionsByMonth(txs)
    ])
  );

  return Object.entries(monthlyAggregatedVendors).map(([key, value]) => {
    const vendorObj = vendors.find(({ id }) => id === key);
    return {
      companyId: vendorObj?.vendorId || key,
      aggregatedTransactions: value
    };
  });
};
